/**
 * Alias Migration Hook
 * 
 * Auto-triggers alias migration for authenticated users on first login.
 * Re-links anonymous game records to the user's authenticated account.
 */

import { useEffect, useState } from 'react';
import apiClient from '../services/apiClient';
import { useAuth } from './useAuth';

interface MigrationResult {
  completed: boolean;
  recordsUpdated: number;
  error?: string;
}

/**
 * Hook to check and trigger alias migration
 * 
 * Usage:
 * ```tsx
 * function Dashboard() {
 *   const { migrationStatus, isMigrating } = useAliasMigration();
 *   
 *   if (isMigrating) {
 *     return <div>Syncing your game history...</div>;
 *   }
 *   
 *   return <div>Dashboard content</div>;
 * }
 * ```
 */
export function useAliasMigration() {
  const { user, isLoading: authLoading } = useAuth();
  const [isMigrating, setIsMigrating] = useState(false);
  const [migrationStatus, setMigrationStatus] = useState<MigrationResult | null>(null);

  useEffect(() => {
    const checkAndMigrate = async () => {
      // Wait for auth to initialize
      if (authLoading) return;
      
      // Only migrate for authenticated users
      if (!user) return;

      try {
        // Check if migration already completed
        const statusResponse = await apiClient.get('/migration/aliases/status');
        const migrationCompleted = statusResponse.data.data.migrationCompleted;

        if (migrationCompleted) {
          setMigrationStatus({ completed: true, recordsUpdated: 0 });
          return;
        }

        // Migration needed - trigger it
        console.log('[useAliasMigration] Starting migration for user:', user.username);
        setIsMigrating(true);

        const migrationResponse = await apiClient.post('/migration/aliases/user');
        
        if (migrationResponse.data.success) {
          const recordsUpdated = migrationResponse.data.data.recordsUpdated;
          console.log('[useAliasMigration] Migration completed:', recordsUpdated, 'records updated');
          
          setMigrationStatus({
            completed: true,
            recordsUpdated,
          });

          // Optional: Show success notification
          if (recordsUpdated > 0) {
            console.log(`✅ Successfully migrated ${recordsUpdated} game records to your account!`);
            // You can integrate with a toast/notification system here:
            // toast.success(`Migrated ${recordsUpdated} game records to your account`);
          }
        } else {
          throw new Error(migrationResponse.data.message || 'Migration failed');
        }
      } catch (error) {
        console.error('[useAliasMigration] Migration error:', error);
        setMigrationStatus({
          completed: false,
          recordsUpdated: 0,
          error: error instanceof Error ? error.message : 'Unknown error',
        });
      } finally {
        setIsMigrating(false);
      }
    };

    checkAndMigrate();
  }, [user, authLoading]);

  return {
    isMigrating,
    migrationStatus,
    isCompleted: migrationStatus?.completed ?? false,
    recordsUpdated: migrationStatus?.recordsUpdated ?? 0,
    error: migrationStatus?.error,
  };
}

/**
 * Optional: Component wrapper for displaying migration status
 * 
 * Usage:
 * ```tsx
 * <AliasMigrationBanner />
 * ```
 */
export function AliasMigrationBanner() {
  const { isMigrating, migrationStatus } = useAliasMigration();

  if (!isMigrating && !migrationStatus) return null;

  if (isMigrating) {
    return (
      <div style={{
        padding: '12px 16px',
        background: 'rgba(102, 126, 234, 0.1)',
        border: '1px solid rgba(102, 126, 234, 0.3)',
        borderRadius: '8px',
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
      }}>
        <div className="spinner" style={{ fontSize: '16px' }}>⏳</div>
        <div>
          <div style={{ fontWeight: 600, color: 'rgba(255,255,255,0.9)' }}>
            Syncing your game history...
          </div>
          <div style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.7)' }}>
            Re-linking your past games to your account
          </div>
        </div>
      </div>
    );
  }

  if (migrationStatus?.completed && migrationStatus.recordsUpdated > 0) {
    return (
      <div style={{
        padding: '12px 16px',
        background: 'rgba(34, 197, 94, 0.1)',
        border: '1px solid rgba(34, 197, 94, 0.3)',
        borderRadius: '8px',
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
      }}>
        <div style={{ fontSize: '20px' }}>✅</div>
        <div>
          <div style={{ fontWeight: 600, color: 'rgba(255,255,255,0.9)' }}>
            Game history synced!
          </div>
          <div style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.7)' }}>
            {migrationStatus.recordsUpdated} past game records added to your account
          </div>
        </div>
      </div>
    );
  }

  if (migrationStatus?.error) {
    return (
      <div style={{
        padding: '12px 16px',
        background: 'rgba(239, 68, 68, 0.1)',
        border: '1px solid rgba(239, 68, 68, 0.3)',
        borderRadius: '8px',
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
      }}>
        <div style={{ fontSize: '20px' }}>⚠️</div>
        <div>
          <div style={{ fontWeight: 600, color: 'rgba(255,255,255,0.9)' }}>
            Migration failed
          </div>
          <div style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.7)' }}>
            {migrationStatus.error}
          </div>
        </div>
      </div>
    );
  }

  return null;
}

export default useAliasMigration;
